<script setup>
import { ref, computed, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { emptyContactReferences } from '@/composables/company-resolve'

const props = defineProps({
  modelValue: { type: [Number, String, null], default: null },
  companyId: { type: [Number, String, null], default: null },
  label: { type: String, default: null },
})
const emit = defineEmits(['update:modelValue', 'load-references'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const { referenceTypeEnum } = planningStore
const webSiteStore = useWebSiteStore()

const contacts = ref([])
const filterText = ref('')
const selectedLabel = ref('')

const options = computed(() => contacts.value
  .filter(c => !c.archived || c.id === props.modelValue)
  .map(c => ({ id: c.id, name: planningStore.getContactName(c), role: c.role,
    references: { [referenceTypeEnum.PHONE]: c.default_phone ?? '', [referenceTypeEnum.EMAIL]: c.default_email ?? '' },
  })))

const filteredOptions = computed(() => filterText.value
  ? options.value.filter(o => o.name.toLowerCase().includes(filterText.value))
  : options.value)

const optionLabel = (value) => options.value.find(o => o.id === value)?.name ?? value ?? ''

watch(() => props.companyId, async(companyId) => {
  contacts.value = typeof companyId === 'number'
    ? (await planningStore.fetchContactsByCompanyAsync(companyId)).data
    : []
}, { immediate: true })

watch([() => props.modelValue, options], ([value]) => {
  selectedLabel.value = optionLabel(value)
  if (typeof value === 'number') emit('load-references', { ...(options.value.find(o => o.id === value)?.references ?? emptyContactReferences()) })
  else if (value === null) emit('load-references', emptyContactReferences())
}, { immediate: true })

const onUpdate = (value) => {
  selectedLabel.value = optionLabel(value)
  emit('update:modelValue', value)
}

const onFilter = (val, update) => {
  update(() => { filterText.value = val.toLowerCase() })
}

const onInputValue = (val) => {
  if (val === selectedLabel.value) return
  const name = val.trim()
  const existing = options.value.find(o => o.name.toLowerCase() === name.toLowerCase())
  if (existing) {
    onUpdate(existing.id)
    return
  }
  selectedLabel.value = val
  emit('update:modelValue', name || null)
  emit('load-references', emptyContactReferences())
}
</script>

<template>
  <q-select
    :model-value="modelValue"
    :label="label || i18n.t('common.contact')"
    :options="filteredOptions"
    option-value="id"
    option-label="name"
    emit-value
    map-options
    clearable
    use-input
    fill-input
    hide-selected
    :input-debounce="webSiteStore.getInputDebounce()"
    @filter="onFilter"
    @input-value="onInputValue"
    @update:model-value="onUpdate"
  >
    <template #option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section>
          <q-item-label>{{ scope.opt.name }}</q-item-label>
          <q-item-label v-if="scope.opt.role" caption>{{ scope.opt.role }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>
